import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Alert,
    Button,
    DatePicker,
    Form,
    Input,
    InputNumber,
    Modal,
    Popconfirm,
    Select,
    Space,
    Switch,
    Table,
    Tabs,
    Tag,
    Tooltip,
} from 'antd';
import {
    CalendarOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    DeleteOutlined,
    EditOutlined,
    MinusCircleOutlined,
    PlusOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { isMobile } from 'react-device-detect';
import {
    deleteConditionSource,
    deleteServiceCondition,
    deleteServiceGroup as deleteServiceGroupAction,
    deleteServiceGroupCondition,
    deleteServiceGroupMaterial,
    fetchServiceGroupConfiguration,
    fetchServiceGroupConfigurationCatalogs,
    fetchConditionSources,
    saveServiceCondition,
    saveServiceGroup as saveServiceGroupAction,
    saveServiceGroupCondition,
    saveServiceGroupMaterial,
    saveConditionSource,
} from '@/redux/actions/serviceGroupConfigurationActions';

const { TabPane } = Tabs;

const toDate = value => (value ? moment(value, 'YYYY-MM-DD') : null);
const normalizeConditionInputType = value => String(value || 'boolean').trim().toLowerCase();

const renderStatus = value => (
    <Tooltip title={value ? 'Đang hoạt động' : 'Ngừng hoạt động'}>
        {value
            ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
            : <CloseCircleOutlined style={{ color: '#ff4d4f', fontSize: 16 }} />}
    </Tooltip>
);

const renderRequired = value => (
    <Tooltip title={value ? 'Bắt buộc' : 'Không bắt buộc'}>
        {value
            ? <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 16 }} />
            : <MinusCircleOutlined style={{ color: '#8c8c8c', fontSize: 16 }} />}
    </Tooltip>
);

const renderRequireDate = value => (
    <Tooltip title={value ? 'Yêu cầu ngày' : 'Không yêu cầu ngày'}>
        {value
            ? <CalendarOutlined style={{ color: '#1890ff', fontSize: 16 }} />
            : <MinusCircleOutlined style={{ color: '#8c8c8c', fontSize: 16 }} />}
    </Tooltip>
);

export default function ServiceGroupConfigurationModal({ visible, onCancel, allowUpdate, onCatalogChange }) {
    const dispatch = useDispatch();
    const catalogs = useSelector(state => state.serviceGroupConfigurationReducer.catalogs);
    const configuration = useSelector(state => state.serviceGroupConfigurationReducer.configuration);
    const [serviceGroupId, setServiceGroupId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [conditionForm] = Form.useForm();
    const [materialForm] = Form.useForm();
    const [catalogForm] = Form.useForm();
    const [serviceGroupForm] = Form.useForm();
    const [sourceForm] = Form.useForm();
    const [editingCondition, setEditingCondition] = useState(null);
    const [editingMaterial, setEditingMaterial] = useState(null);
    const [editingCatalogCondition, setEditingCatalogCondition] = useState(null);
    const [editingServiceGroup, setEditingServiceGroup] = useState(null);
    const [conditionModalVisible, setConditionModalVisible] = useState(false);
    const [materialModalVisible, setMaterialModalVisible] = useState(false);
    const [catalogModalVisible, setCatalogModalVisible] = useState(false);
    const [serviceGroupModalVisible, setServiceGroupModalVisible] = useState(false);
    const [conditionSources, setConditionSources] = useState([]);
    const [editingSource, setEditingSource] = useState(null);
    const [sourceModalVisible, setSourceModalVisible] = useState(false);

    const loadCatalogs = useCallback(async () => {
        const data = await dispatch(fetchServiceGroupConfigurationCatalogs());
        setConditionSources(await dispatch(fetchConditionSources()));
        setServiceGroupId(current => (
            data.service_groups?.some(item => String(item.id) === String(current))
                ? current
                : data.service_groups?.[0]?.id || null
        ));
        return data;
    }, [dispatch]);

    const reloadConditionSources = async () => setConditionSources(await dispatch(fetchConditionSources()));

    const openSourceForm = item => {
        setEditingSource(item || null);
        sourceForm.setFieldsValue({
            service_condition_id: item?.service_condition_id,
            source_type: item?.source_type,
            folder_type_id: item?.folder_type_id,
            custom_folder_id: item?.custom_folder_id,
            status: item ? !!item.status : true,
        });
        setSourceModalVisible(true);
    };

    const submitSource = async values => {
        try {
            await dispatch(saveConditionSource(editingSource?.id, { ...values, mapping_type: editingSource?.mapping_type }));
            setSourceModalVisible(false);
            await reloadConditionSources();
        } catch (error) {
            // Action đã hiển thị lỗi.
        }
    };

    const stopSource = async item => {
        try {
            await dispatch(deleteConditionSource(item.id, item.mapping_type));
            await reloadConditionSources();
        } catch (error) {
            // Action đã hiển thị lỗi.
        }
    };

    const loadConfiguration = useCallback(async id => {
        if (!id) return;
        setLoading(true);
        try {
            await dispatch(fetchServiceGroupConfiguration(id));
        } catch (error) {
            // Action đã hiển thị lỗi.
        } finally {
            setLoading(false);
        }
    }, [dispatch]);

    useEffect(() => {
        if (!visible) return;
        loadCatalogs().catch(() => { });
    }, [dispatch, visible, loadCatalogs]);

    useEffect(() => {
        if (visible && serviceGroupId) {
            loadConfiguration(serviceGroupId);
        }
    }, [dispatch, visible, serviceGroupId, loadConfiguration]);

    const openConditionForm = item => {
        setEditingCondition(item || null);
        conditionForm.setFieldsValue({
            service_condition_id: item?.service_condition_id,
            required: item ? !!item.required : true,
            status: item ? !!item.status : true,
            sort_order: item?.sort_order || 0,
        });
        setConditionModalVisible(true);
    };

    const saveCondition = async values => {
        const payload = { ...values, service_group_id: serviceGroupId };
        try {
            await dispatch(saveServiceGroupCondition(editingCondition?.id, payload));
            setConditionModalVisible(false);
            await loadConfiguration(serviceGroupId);
        } catch (error) {
            // Action đã hiển thị lỗi.
        }
    };

    const deleteCondition = async id => {
        try {
            await dispatch(deleteServiceGroupCondition(id));
            await loadConfiguration(serviceGroupId);
        } catch (error) {
            // Action đã hiển thị lỗi.
        }
    };

    const openConditionCatalogForm = item => {
        setEditingCatalogCondition(item || null);
        catalogForm.setFieldsValue({
            code: item?.code || '',
            name: item?.name || '',
            description: item?.description || '',
            input_type: normalizeConditionInputType(item?.input_type),
            require_date: item ? !!item.require_date : false,
            status: item ? !!item.status : true,
        });
        setCatalogModalVisible(true);
    };

    const saveConditionCatalog = async values => {
        try {
            await dispatch(saveServiceCondition(editingCatalogCondition?.id, {
                ...values,
                input_type: normalizeConditionInputType(values.input_type),
            }));
            setCatalogModalVisible(false);
            catalogForm.resetFields();
            await loadCatalogs();
            await loadConfiguration(serviceGroupId);
        } catch (error) {
            // Action đã hiển thị lỗi.
        }
    };

    const deleteConditionCatalog = async id => {
        try {
            await dispatch(deleteServiceCondition(id));
            await loadCatalogs();
            await loadConfiguration(serviceGroupId);
        } catch (error) {
            // Action đã hiển thị lỗi.
        }
    };

    const openServiceGroupForm = item => {
        setEditingServiceGroup(item || null);
        serviceGroupForm.setFieldsValue({
            code: item?.code || '',
            name: item?.name || '',
            color: item?.color || 'default',
            status: item ? !!item.status : true,
        });
        setServiceGroupModalVisible(true);
    };

    const saveServiceGroup = async values => {
        try {
            await dispatch(saveServiceGroupAction(editingServiceGroup?.id, values));
            setServiceGroupModalVisible(false);
            serviceGroupForm.resetFields();
            await loadCatalogs();
            if (onCatalogChange) await onCatalogChange();
        } catch (error) {
            // Action đã hiển thị lỗi.
        }
    };

    const deleteServiceGroup = async id => {
        try {
            await dispatch(deleteServiceGroupAction(id));
            await loadCatalogs();
            if (onCatalogChange) await onCatalogChange();
        } catch (error) {
            // Action đã hiển thị lỗi.
        }
    };

    const openMaterialForm = item => {
        setEditingMaterial(item || null);
        materialForm.setFieldsValue({
            product_group_id: item?.product_group_id,
            component: item?.component || null,
            required: item ? !!item.required : true,
            status: item ? !!item.status : true,
            minimum_quantity: Number(item?.minimum_quantity ?? 1),
            effective_from: toDate(item?.effective_from),
            effective_to: toDate(item?.effective_to),
        });
        setMaterialModalVisible(true);
    };

    const saveMaterial = async values => {
        const payload = {
            ...values,
            service_group_id: serviceGroupId,
            effective_from: values.effective_from?.format('YYYY-MM-DD') || null,
            effective_to: values.effective_to?.format('YYYY-MM-DD') || null,
        };
        try {
            await dispatch(saveServiceGroupMaterial(editingMaterial?.id, payload));
            setMaterialModalVisible(false);
            await loadConfiguration(serviceGroupId);
        } catch (error) {
            // Action đã hiển thị lỗi.
        }
    };

    const deleteMaterial = async id => {
        try {
            await dispatch(deleteServiceGroupMaterial(id));
            await loadConfiguration(serviceGroupId);
        } catch (error) {
            // Action đã hiển thị lỗi.
        }
    };

    const conditionColumns = [
        { title: 'Điều kiện', dataIndex: ['service_condition', 'name'] },
        { title: 'Mã tra cứu', dataIndex: ['service_condition', 'code'], width: 180 },
        { title: 'Thứ tự', dataIndex: 'sort_order', width: 80, align: 'center' },
        { title: 'Bắt buộc', dataIndex: 'required', width: 95, align: 'center', render: renderRequired },
        { title: 'Bật', dataIndex: 'status', width: 75, align: 'center', render: renderStatus },
        {
            title: '#', width: 120, align: 'center', render: (_, item) => allowUpdate && (
                <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => openConditionForm(item)} />
                    <Popconfirm title="Ngừng áp dụng điều kiện này cho nhóm dịch vụ?" onConfirm={() => deleteCondition(item.id)}>
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            )
        },
    ];

    const materialColumns = [
        { title: 'Nhóm vật tư', dataIndex: ['product_group', 'name'] },
        { title: 'Phần công việc', dataIndex: 'component', width: 150, render: value => value ? <Tag>{value}</Tag> : 'Áp dụng chung' },
        { title: 'Số lượng tối thiểu', dataIndex: 'minimum_quantity', width: 160, align: 'right' },
        { title: 'Hiệu lực từ', dataIndex: 'effective_from', width: 110, render: value => value ? moment(value).format('DD/MM/YYYY') : '-' },
        { title: 'Đến', dataIndex: 'effective_to', width: 110, render: value => value ? moment(value).format('DD/MM/YYYY') : '-' },
        { title: 'Bắt buộc', dataIndex: 'required', width: 95, align: 'center', render: renderRequired },
        { title: 'Bật', dataIndex: 'status', width: 75, align: 'center', render: renderStatus },
        {
            title: '#', width: 120, align: 'center', render: (_, item) => allowUpdate && (
                <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => openMaterialForm(item)} />
                    <Popconfirm title="Ngừng áp dụng yêu cầu vật tư này?" onConfirm={() => deleteMaterial(item.id)}>
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            )
        },
    ];

    const conditionCatalogColumns = [
        { title: 'Tên điều kiện', dataIndex: 'name' },
        { title: 'Mã tra cứu', dataIndex: 'code', width: 240 },
        { title: 'Cách ghi nhận', dataIndex: 'input_type', width: 130, render: value => <Tag>{({ boolean: 'Có / Không', number: 'Số' }[normalizeConditionInputType(value)] || 'Theo cấu hình')}</Tag> },
        { title: 'Có ngày', dataIndex: 'require_date', width: 90, align: 'center', render: renderRequireDate },
        { title: 'Mô tả', dataIndex: 'description' },
        { title: 'Kích hoạt', dataIndex: 'status', width: 120, align: 'center', render: renderStatus },
        {
            title: '#', width: 120, align: 'center', render: (_, item) => allowUpdate && (
                <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => openConditionCatalogForm(item)} />
                    <Popconfirm title="Xóa điều kiện này?" onConfirm={() => deleteConditionCatalog(item.id)}>
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            )
        },
    ];

    const serviceGroupColumns = [
        { title: 'Tên nhóm', dataIndex: 'name' },
        { title: 'Mã tra cứu', dataIndex: 'code', width: 200 },
        { title: 'Màu', dataIndex: 'color', width: 150, render: (value, item) => <Tag color={value || 'default'}>{item.name}</Tag> },
        { title: 'Kích hoạt', dataIndex: 'status', width: 120, align: 'center', render: renderStatus },
        {
            title: '#', width: 120, align: 'center', render: (_, item) => allowUpdate && (
                <Space>
                    <Button size="small" icon={<EditOutlined />} onClick={() => openServiceGroupForm(item)} />
                    <Popconfirm title="Ngừng sử dụng nhóm dịch vụ này? Dữ liệu lịch sử vẫn được giữ lại." onConfirm={() => deleteServiceGroup(item.id)}>
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            )
        },
    ];

    return (
        <>
            <Modal
                visible={visible}
                title="Cấu hình theo nhóm dịch vụ"
                width="92%"
                footer={null}
                onCancel={onCancel}
                destroyOnClose
                maskClosable={false}
            >
                <Alert
                    type="warning"
                    style={{ marginBottom: 16 }}
                    message="Cấu hình dữ liệu theo nhóm dịch vụ"
                    description="Chọn một nhóm dịch vụ để thiết lập các điều kiện chuyên môn và vật tư cần áp dụng. Các cấu hình này được dùng làm tiêu chuẩn kiểm tra dữ liệu thực tế của từng dịch vụ khách hàng."
                />
                <Space style={{ marginBottom: 16 }}>
                    <span>Nhóm dịch vụ:</span>
                    <Select
                        showSearch
                        optionFilterProp="children"
                        style={{ width: 320 }}
                        value={serviceGroupId}
                        onChange={setServiceGroupId}
                    >
                        {(catalogs.service_groups || []).map(item => <Select.Option key={item.id} value={item.id}>{item.name}</Select.Option>)}
                    </Select>
                </Space>

                <Tabs defaultActiveKey="conditions">
                    <TabPane tab="Cấu hình điều kiện chuyên môn" key="conditions">
                        <Alert
                            type="warning"
                            style={{ marginBottom: 12 }}
                            message="Thiết lập các điều kiện cần áp dụng cho nhóm dịch vụ đã chọn, ví dụ: đã cắm trụ Implant, đã hoàn thành phục hình, đã gắn mắc cài hoặc có đủ hồ sơ cần thiết."
                        />
                        <Space style={{ marginBottom: 12 }}>
                            <Button type="primary" icon={<PlusOutlined />} disabled={!allowUpdate || !serviceGroupId} onClick={() => openConditionForm(null)}>Thêm điều kiện áp dụng</Button>
                        </Space>
                        <Table rowKey="id" loading={loading} pagination={false} columns={conditionColumns} dataSource={configuration.service_group_conditions || []} />
                    </TabPane>
                    <TabPane tab="Cấu hình vật tư áp dụng" key="materials">
                        <Alert
                            type="warning"
                            style={{ marginBottom: 12 }}
                            message="Thiết lập các nhóm vật tư cần có để hệ thống kiểm tra điều kiện thực hiện của dịch vụ."
                        />
                        <Space style={{ marginBottom: 12 }} wrap>
                            <Button type="primary" icon={<PlusOutlined />} disabled={!allowUpdate || !serviceGroupId} onClick={() => openMaterialForm(null)}>Thêm yêu cầu vật tư</Button>
                        </Space>
                        <Table rowKey="id" loading={loading} pagination={false} columns={materialColumns} dataSource={configuration.materials || []} scroll={{ x: 1100 }} />
                    </TabPane>
                    <TabPane tab="Danh sách điều kiện chuyên môn" key="condition-catalog">
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            disabled={!allowUpdate}
                            onClick={() => openConditionCatalogForm(null)}
                            style={{ marginBottom: 12 }}
                        >
                            Thêm điều kiện
                        </Button>
                        <Table
                            rowKey="id"
                            pagination={false}
                            columns={conditionCatalogColumns}
                            dataSource={catalogs.service_conditions || []}
                        />
                    </TabPane>
                    <TabPane tab="Nguồn tự động của điều kiện" key="condition-sources">
                        <Alert type="warning" style={{ marginBottom: 12 }}
                            message="Chọn nơi hệ thống tự kiểm tra điều kiện"
                            description="Điều kiện có nguồn tự động sẽ chỉ đọc trên dịch vụ khách hàng. Hệ thống tự xác nhận từ hồ sơ hoặc ảnh đã duyệt và không lưu thêm trạng thái thủ công trùng lặp." />
                        <Button type="primary" icon={<PlusOutlined />} disabled={!allowUpdate} onClick={() => openSourceForm(null)} style={{ marginBottom: 12 }}>Thêm nguồn tự động</Button>
                        <Table rowKey={item => `${item.mapping_type}-${item.id}`} pagination={false} dataSource={conditionSources} columns={[
                            { title: 'Điều kiện', dataIndex: 'condition_name' },
                            { title: 'Nguồn xác nhận', render: (_, item) => (catalogs.condition_source_types || []).find(option => option.value === item.source_type)?.label || item.source_type },
                            { title: 'Phạm vi nguồn', render: (_, item) => item.folder_type_name || item.custom_folder_name || 'Theo dịch vụ/khách hàng đúng nghiệp vụ' },
                            { title: 'Trạng thái', dataIndex: 'status', width: 120, render: renderStatus },
                            { title: 'Thao tác', width: 180, render: (_, item) => <Space><Button size="small" icon={<EditOutlined />} disabled={!allowUpdate} onClick={() => openSourceForm(item)}>Sửa</Button>{item.status ? <Popconfirm title="Ngừng dùng nguồn tự động này? Dữ liệu lịch sử không bị xóa." onConfirm={() => stopSource(item)}><Button size="small" danger disabled={!allowUpdate}>Ngừng</Button></Popconfirm> : null}</Space> },
                        ]} />
                    </TabPane>
                    <TabPane tab="Nhóm dịch vụ" key="service-groups">
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            disabled={!allowUpdate}
                            onClick={() => openServiceGroupForm(null)}
                            style={{ marginBottom: 12 }}
                        >
                            Thêm nhóm dịch vụ
                        </Button>
                        <Table
                            rowKey="id"
                            pagination={false}
                            columns={serviceGroupColumns}
                            dataSource={catalogs.service_groups || []}
                        />
                    </TabPane>
                </Tabs>
            </Modal>

            <Modal visible={sourceModalVisible} title={editingSource ? 'Sửa nguồn tự động' : 'Thêm nguồn tự động'} onCancel={() => setSourceModalVisible(false)} onOk={() => sourceForm.submit()} maskClosable={false} destroyOnClose>
                <Form form={sourceForm} layout="vertical" onFinish={submitSource}>
                    <Form.Item name="service_condition_id" label="Điều kiện được xác nhận" rules={[{ required: true, message: 'Chọn điều kiện chuyên môn.' }]}>
                        <Select showSearch optionFilterProp="children" disabled={!!editingSource}>{(catalogs.service_conditions || []).filter(item => item.status).map(item => <Select.Option key={item.id} value={item.id}>{item.name}</Select.Option>)}</Select>
                    </Form.Item>
                    <Form.Item name="source_type" label="Hệ thống xác nhận từ" rules={[{ required: true, message: 'Chọn nguồn xác nhận.' }]}>
                        <Select disabled={!!editingSource}>{(catalogs.condition_source_types || []).filter(item => item.value !== 'MANUAL').map(item => <Select.Option key={item.value} value={item.value}>{item.label}</Select.Option>)}</Select>
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate>{({ getFieldValue }) => getFieldValue('source_type') === 'IMAGE_FOLDER_TYPE' && <Form.Item name="folder_type_id" label="Loại thư mục ảnh" rules={[{ required: true, message: 'Chọn loại thư mục ảnh.' }]}><Select showSearch optionFilterProp="children">{(catalogs.image_folder_types || []).map(item => <Select.Option key={item.id} value={item.id}>{item.name}</Select.Option>)}</Select></Form.Item>}</Form.Item>
                    <Form.Item noStyle shouldUpdate>{({ getFieldValue }) => getFieldValue('source_type') === 'IMAGE_CUSTOM_FOLDER' && <Form.Item name="custom_folder_id" label="Thư mục ảnh tùy chỉnh" rules={[{ required: true, message: 'Chọn thư mục ảnh.' }]}><Select showSearch optionFilterProp="children">{(catalogs.image_custom_folders || []).map(item => <Select.Option key={item.id} value={item.id}>{item.name}</Select.Option>)}</Select></Form.Item>}</Form.Item>
                    <Form.Item name="status" label="Đang áp dụng" valuePropName="checked"><Switch /></Form.Item>
                </Form>
            </Modal>

            <Modal visible={conditionModalVisible} title={editingCondition ? 'Sửa mapping điều kiện' : 'Thêm mapping điều kiện'} onCancel={() => setConditionModalVisible(false)} onOk={() => conditionForm.submit()} maskClosable={false} destroyOnClose>
                <Form form={conditionForm} layout="vertical" onFinish={saveCondition}>
                    <Form.Item name="service_condition_id" label="Điều kiện" rules={[{ required: true, message: 'Vui lòng chọn điều kiện.' }]}>
                        <Select showSearch optionFilterProp="children">
                            {(catalogs.service_conditions || []).map(item => <Select.Option key={item.id} value={item.id}>{item.name} ({item.code})</Select.Option>)}
                        </Select>
                    </Form.Item>
                    <Form.Item name="sort_order" label="Thứ tự"><InputNumber min={0} precision={0} style={{ width: '100%' }} /></Form.Item>
                    <Form.Item name="required" label="Bắt buộc" valuePropName="checked"><Switch /></Form.Item>
                    <Form.Item name="status" label="Kích hoạt" valuePropName="checked"><Switch /></Form.Item>
                </Form>
            </Modal>

            <Modal maskClosable={false} visible={catalogModalVisible} title={editingCatalogCondition ? 'Sửa điều kiện chuyên môn' : 'Thêm điều kiện chuyên môn'} onCancel={() => setCatalogModalVisible(false)} onOk={() => catalogForm.submit()} width={isMobile ? '100%' : '50%'} destroyOnClose>
                <Form form={catalogForm} layout="vertical" onFinish={saveConditionCatalog}>
                    <Form.Item name="code" label="Mã điều kiện" rules={[{ required: true, pattern: /^[a-z0-9_]+$/, message: 'Chỉ dùng chữ thường, số và dấu gạch dưới.' }]}><Input placeholder="Ví dụ: gum_check" /></Form.Item>
                    <Form.Item name="name" label="Tên điều kiện" rules={[{ required: true, message: 'Vui lòng nhập tên.' }]}><Input /></Form.Item>
                    <Form.Item name="description" label="Mô tả"><Input.TextArea /></Form.Item>
                    <Form.Item name="input_type" label="Kiểu nhập liệu" rules={[{ required: true, message: 'Vui lòng chọn kiểu nhập.' }]}>
                        <Select>
                            <Select.Option value="boolean">Có / Không</Select.Option>
                            <Select.Option value="number">Số</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="require_date" label="Yêu cầu ngày phát sinh" valuePropName="checked"><Switch /></Form.Item>
                    <Form.Item name="status" label="Kích hoạt" valuePropName="checked"><Switch /></Form.Item>
                </Form>
            </Modal>

            <Modal maskClosable={false} visible={serviceGroupModalVisible} title={editingServiceGroup ? 'Sửa nhóm dịch vụ' : 'Thêm nhóm dịch vụ'} onCancel={() => setServiceGroupModalVisible(false)} onOk={() => serviceGroupForm.submit()} destroyOnClose>
                <Form form={serviceGroupForm} layout="vertical" onFinish={saveServiceGroup}>
                    <Form.Item name="code" label="Mã nhóm dịch vụ" rules={[{ required: true, pattern: /^[a-z0-9_]+$/, message: 'Chỉ dùng chữ thường, số và dấu gạch dưới.' }]}><Input placeholder="Ví dụ: pediatric_dentistry" /></Form.Item>
                    <Form.Item name="name" label="Tên nhóm dịch vụ" rules={[{ required: true, message: 'Vui lòng nhập tên nhóm.' }]}><Input /></Form.Item>
                    <Form.Item name="color" label="Màu hiển thị" extra="Nhập tên màu Ant Design (blue, purple...) hoặc mã HEX."><Input placeholder="blue" /></Form.Item>
                    <Form.Item name="status" label="Kích hoạt" valuePropName="checked"><Switch /></Form.Item>
                </Form>
            </Modal>

            <Modal maskClosable={false} visible={materialModalVisible} title={editingMaterial ? 'Sửa mapping vật tư' : 'Thêm mapping vật tư'} onCancel={() => setMaterialModalVisible(false)} onOk={() => materialForm.submit()} width={620} destroyOnClose>
                <Form form={materialForm} layout="vertical" onFinish={saveMaterial}>
                    <Form.Item name="product_group_id" label="Nhóm vật tư" rules={[{ required: true, message: 'Vui lòng chọn nhóm vật tư.' }]}>
                        <Select showSearch optionFilterProp="children">
                            {(catalogs.product_groups || []).map(item => <Select.Option key={item.id} value={item.id}>{item.name}</Select.Option>)}
                        </Select>
                    </Form.Item>
                    <Form.Item name="component" label="Thành phần Implant" extra="Chọn thành phần Implant mà điều kiện này áp dụng. Để trống nếu áp dụng chung cho toàn bộ dịch vụ Implant.">
                        <Select allowClear placeholder="Không phân biệt thành phần">
                            <Select.Option value="implant">Cấy trụ</Select.Option>
                            <Select.Option value="restoration">Phục hình</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="minimum_quantity" label="Số lượng tối thiểu" rules={[{ required: true, message: 'Vui lòng nhập số lượng tối thiểu.' }]}><InputNumber min={0} style={{ width: '100%' }} /></Form.Item>
                    <Space size="large">
                        <Form.Item name="effective_from" label="Hiệu lực từ"><DatePicker format="DD/MM/YYYY" /></Form.Item>
                        <Form.Item name="effective_to" label="Hiệu lực đến"><DatePicker format="DD/MM/YYYY" /></Form.Item>
                    </Space>
                    <Form.Item name="required" label="Bắt buộc" valuePropName="checked"><Switch /></Form.Item>
                    <Form.Item name="status" label="Kích hoạt" valuePropName="checked"><Switch /></Form.Item>
                </Form>
            </Modal>
        </>
    );
}
